/** Shared CheckCOA verification PNG renderer. */

import QRCode from 'qrcode';
import { formatProviderLabel } from '../utils/presentationLabels';
import { toCorsImageUrl } from '../utils/imageProxy';

export type CertificateVerdict = 'verified' | 'checked' | 'action' | 'alert';

export interface CertificatePhoto {
  src: string;
  label?: string;
  source?: string;
  seller?: string;
}

export interface CertificateData {
  company: string;
  certificateNumber: string;
  scanDate: string;
  verificationStatus: 'Valid' | 'Invalid' | 'Unknown';
  verdict?: CertificateVerdict;
  title?: string;
  subtitle?: string;
  scanMethod?: string;
  verificationUrl?: string;
  publicId?: string;
  fraudSummary?: string;
  fraudSource?: string;
  fraudSeller?: string;
  fraudEvidence?: CertificatePhoto[];
  fraudMoreCount?: number;
  photos?: CertificatePhoto[];
  metrics?: Array<{ label: string; value: string }>;
  detailRows?: Array<{ label: string; value: string }>;
  details: {
    grade?: string;
    player?: string;
    signer?: string;
    year?: string;
    set?: string;
    itemType?: string;
  };
  imageUrl?: string;
  userId: string;
  scanId: string;
}

type LoadedPhoto = CertificatePhoto & { image: HTMLImageElement };
type DetailLayout = { label: string; value: string; lines: string[]; height: number };

const WIDTH = 1600;
const MIN_HEIGHT = 1600;
const MAX_HEIGHT = 2400;
const GENERIC_VERIFY_URL = 'https://checkcoa.com/';

function safe(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

async function loadImage(src: string): Promise<HTMLImageElement | null> {
  if (!src) return null;
  try {
    const remote = /^https?:\/\//i.test(src) ? toCorsImageUrl(src) : src;
    if (!remote) return null;
    const controller = new AbortController();
    const fetchTimeout = window.setTimeout(() => controller.abort(), 8000);
    const response = await fetch(remote, { mode: 'cors', signal: controller.signal }).finally(() => window.clearTimeout(fetchTimeout));
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) return null;
    const objectUrl = URL.createObjectURL(blob);
    try {
      return await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        const decodeTimeout = window.setTimeout(() => reject(new Error('Image decode timed out.')), 5000);
        image.onload = () => { window.clearTimeout(decodeTimeout); resolve(image); };
        image.onerror = () => { window.clearTimeout(decodeTimeout); reject(new Error('Image decode failed.')); };
        image.src = objectUrl;
      });
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    return null;
  }
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  value: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const text = safe(value);
  if (!text) return [];
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  let consumed = 0;
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (!line || ctx.measureText(candidate).width <= maxWidth) {
      line = candidate;
      consumed += 1;
      continue;
    }
    lines.push(line);
    if (lines.length >= maxLines) break;
    line = word;
    consumed += 1;
  }
  if (line && lines.length < maxLines) lines.push(line);
  if (consumed < words.length && lines.length) {
    let last = lines[lines.length - 1];
    while (last.length > 1 && ctx.measureText(`${last}…`).width > maxWidth) last = last.slice(0, -1);
    lines[lines.length - 1] = `${last.trim()}…`;
  }
  return lines;
}

function rounded(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  fill: string,
  stroke?: string,
): void {
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, radius);
  ctx.fillStyle = fill;
  ctx.fill();
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 2;
    ctx.stroke();
  }
}

function drawContained(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
  const drawWidth = image.naturalWidth * scale;
  const drawHeight = image.naturalHeight * scale;
  ctx.drawImage(image, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight);
}

function uniqueDetailRows(data: CertificateData): Array<{ label: string; value: string }> {
  const rows = [
    ...(data.detailRows || []),
    data.details.grade ? { label: 'Grade', value: data.details.grade } : null,
    data.details.player ? { label: 'Player', value: data.details.player } : null,
    data.details.signer ? { label: 'Signer', value: data.details.signer } : null,
    data.details.year ? { label: 'Year', value: data.details.year } : null,
    data.details.set ? { label: 'Set / title', value: data.details.set } : null,
    data.details.itemType ? { label: 'Item type', value: data.details.itemType } : null,
  ].filter((row): row is { label: string; value: string } => Boolean(row && safe(row.value)));
  return rows
    .filter((row, index) => rows.findIndex((candidate) => candidate.label.toLowerCase() === row.label.toLowerCase()) === index)
    .slice(0, 6);
}

/** Generate the single registered 1600px-wide verification PNG. */
export async function generateCertificateImage(
  data: CertificateData,
  addWatermark = false,
): Promise<Blob> {
  const provider = formatProviderLabel(data.company) || 'CheckCOA';
  const verdict: CertificateVerdict = data.verdict
    || (data.verificationStatus === 'Valid' ? 'verified' : data.verificationStatus === 'Unknown' ? 'action' : 'alert');
  const palettes: Record<CertificateVerdict, { color: string; tint: string; ribbon: string; status: string; copy: string }> = {
    verified: {
      color: '#16845d', tint: '#eaf8f1', ribbon: 'VERIFIED · NO ALERT FOUND', status: 'VERIFIED · NO ALERT',
      copy: 'Provider record matched and no CheckCOA fraud alert was found when this image was issued.',
    },
    checked: {
      color: '#3d4b8f', tint: '#eef0fa', ribbon: 'CHECKED BY CHECKCOA', status: 'CHECKED · RECORDS FOUND',
      copy: 'Related reports exist. Review the current live CheckCOA record for context.',
    },
    action: {
      color: '#b66a14', tint: '#fff5df', ribbon: 'ACTION REQUIRED', status: 'REVIEW REQUIRED',
      copy: 'The provider or CheckCOA record requires another review step.',
    },
    alert: {
      color: '#c73f35', tint: '#fff0ee', ribbon: 'FRAUD ALERT', status: 'FRAUD ALERT',
      copy: 'CheckCOA found risk information associated with this certificate number.',
    },
  };
  const palette = palettes[verdict];
  const providerPhotoRequest = (data.photos || []).find((photo) => safe(photo.src))
    || (data.imageUrl ? { src: data.imageUrl, label: `${provider} PROVIDER PHOTO` } : null);
  const [providerImage, logoImage, ...evidenceImages] = await Promise.all([
    providerPhotoRequest ? loadImage(providerPhotoRequest.src) : Promise.resolve(null),
    loadImage('/CheckCOA_white.png'),
    ...(data.fraudEvidence || []).slice(0, 5).map((photo) => loadImage(photo.src)),
  ]);
  const loadedEvidence: LoadedPhoto[] = (data.fraudEvidence || []).slice(0, 5).flatMap((photo, index) => {
    const image = evidenceImages[index];
    return image ? [{ ...photo, image }] : [];
  });

  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = MAX_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable.');

  ctx.font = '800 48px Arial, sans-serif';
  const titleLines = wrapText(ctx, data.title || `Certificate ${data.certificateNumber}`, 1390, 2);
  const titleBottom = 540 + Math.max(0, titleLines.length - 1) * 58;
  const identityY = titleBottom + 55;
  const contentY = identityY + 185;
  const hasProviderPhoto = Boolean(providerImage);
  const detailsX = hasProviderPhoto ? 650 : 95;
  const detailsWidth = hasProviderPhoto ? 855 : 1410;
  const valueX = detailsX + (hasProviderPhoto ? 260 : 290);
  const valueWidth = detailsWidth - (hasProviderPhoto ? 290 : 325);
  ctx.font = '700 26px Arial, sans-serif';
  const detailLayouts: DetailLayout[] = uniqueDetailRows(data).map((row) => {
    const lines = wrapText(ctx, row.value, valueWidth, 2);
    return { ...row, lines, height: lines.length > 1 ? 108 : 82 };
  });
  const unavailableLineHeight = hasProviderPhoto ? 0 : 48;
  const detailsRowsHeight = detailLayouts.reduce((sum, row) => sum + row.height + 12, 0);
  const detailsHeight = 40 + unavailableLineHeight + Math.max(detailLayouts.length ? detailsRowsHeight : 106, 106);
  const contentHeight = Math.max(hasProviderPhoto ? 610 : 0, detailsHeight);
  const resultY = contentY + contentHeight + 35;
  const hasFraudSection = verdict !== 'verified'
    || loadedEvidence.length > 0
    || Boolean(data.fraudMoreCount)
    || Boolean(data.fraudSource)
    || Boolean(data.fraudSeller);
  const fraudY = resultY + 205;
  const fraudHeight = hasFraudSection ? (loadedEvidence.length ? 315 : 175) : 0;
  const footerY = hasFraudSection ? fraudY + fraudHeight + 35 : resultY + 205;
  const finalHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, footerY + 315));
  canvas.height = finalHeight;

  const drawLabel = (label: string, x: number, y: number, color = '#74788a') => {
    ctx.fillStyle = color;
    ctx.font = '700 20px Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(label.toUpperCase(), x, y);
  };
  const drawLines = (lines: string[], x: number, y: number, size: number, lineHeight: number, color = '#11131d') => {
    ctx.fillStyle = color;
    ctx.font = `700 ${size}px Arial, sans-serif`;
    ctx.textAlign = 'left';
    lines.forEach((line, index) => ctx.fillText(line, x, y + index * lineHeight));
  };

  ctx.fillStyle = '#f3f5f9';
  ctx.fillRect(0, 0, WIDTH, finalHeight);
  rounded(ctx, 55, 55, 1490, finalHeight - 110, 34, '#ffffff', '#d9dce6');
  rounded(ctx, 55, 55, 1490, 210, 34, '#0a0b16');
  ctx.fillStyle = '#0a0b16';
  ctx.fillRect(55, 210, 1490, 55);

  if (logoImage) {
    drawContained(ctx, logoImage, 105, 86, 430, 115);
  } else {
    ctx.font = '800 72px Arial, sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.fillText('Check', 115, 165);
    const checkWidth = ctx.measureText('Check').width;
    ctx.fillStyle = '#5b73ff';
    ctx.fillText('COA', 115 + checkWidth, 165);
  }
  ctx.font = '700 21px Arial, sans-serif';
  ctx.fillStyle = '#afb5d0';
  ctx.textAlign = 'right';
  ctx.fillText('VERIFICATION RECORD', 1485, 120);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(`ORIGINAL ${safe(data.publicId || data.scanId).slice(0, 32) || 'CHECKCOA'}`, 1485, 162);

  rounded(ctx, 95, 300, 1410, 105, 18, palette.color);
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(135, 352, 13, 0, Math.PI * 2);
  ctx.fill();
  ctx.font = '800 28px Arial, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(palette.ribbon, 170, 362);
  ctx.font = '400 22px Arial, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText(verdict === 'verified' ? 'Clean at original issue time' : 'Check the current live record', 1460, 360);

  ctx.textAlign = 'left';
  ctx.fillStyle = '#5663db';
  ctx.font = '800 20px Arial, sans-serif';
  ctx.fillText('CERTIFICATE OF VERIFICATION', 105, 475);
  drawLines(titleLines, 105, 540, 48, 58, '#0a0b16');

  rounded(ctx, 95, identityY, 1410, 140, 18, '#f7f8fc', '#e0e2ea');
  const identity = [
    { label: 'Provider', value: provider, width: 330, size: 27 },
    { label: 'Certificate', value: data.certificateNumber, width: 255, size: 27 },
    { label: 'Status', value: palette.status, width: 505, size: 23 },
    {
      label: 'Issued',
      value: Number.isNaN(new Date(data.scanDate).getTime())
        ? safe(data.scanDate)
        : new Date(data.scanDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }),
      width: 320,
      size: 27,
    },
  ];
  let identityX = 125;
  identity.forEach((item, index) => {
    if (index) {
      ctx.strokeStyle = '#dfe1e9';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(identityX - 24, identityY + 26);
      ctx.lineTo(identityX - 24, identityY + 114);
      ctx.stroke();
    }
    drawLabel(item.label, identityX, identityY + 43);
    ctx.font = `700 ${item.size}px Arial, sans-serif`;
    drawLines(wrapText(ctx, item.value, item.width - 45, 1), identityX, identityY + 92, item.size, item.size + 5);
    identityX += item.width;
  });

  if (hasProviderPhoto && providerImage) {
    rounded(ctx, 95, contentY, 510, 610, 22, '#f5f5f3', '#d9d9d4');
    drawContained(ctx, providerImage, 120, contentY + 25, 460, 500);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(97, contentY + 530, 506, 78);
    ctx.font = '800 18px Arial, sans-serif';
    ctx.fillStyle = '#5d6171';
    ctx.textAlign = 'center';
    ctx.fillText(safe(providerPhotoRequest?.label) || `${provider} PROVIDER PHOTO`, 350, contentY + 578);
  }

  drawLabel('Provider record details', detailsX, contentY + 22);
  let detailY = contentY + 48;
  if (!hasProviderPhoto) {
    ctx.fillStyle = '#74788a';
    ctx.font = '500 22px Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Provider photo is not available for this record', detailsX, detailY + 28);
    detailY += unavailableLineHeight;
  }
  if (!detailLayouts.length) {
    rounded(ctx, detailsX, detailY, detailsWidth, 94, 14, '#fafafd', '#e3e4eb');
    ctx.fillStyle = '#74788a';
    ctx.font = '400 24px Arial, sans-serif';
    ctx.fillText('No additional provider fields were returned.', detailsX + 25, detailY + 57);
  } else {
    detailLayouts.forEach((row, index) => {
      rounded(ctx, detailsX, detailY, detailsWidth, row.height, 14, index === 0 ? '#f1f3ff' : '#fafafd', index === 0 ? '#aeb9ff' : '#e3e4eb');
      drawLabel(row.label, detailsX + 24, detailY + 33);
      drawLines(row.lines, valueX, detailY + 47, 26, 33);
      detailY += row.height + 12;
    });
  }

  rounded(ctx, 95, resultY, 1410, 170, 20, palette.tint, palette.color);
  drawLabel(verdict === 'verified' ? 'CheckCOA result' : 'Risk context', 125, resultY + 47, palette.color);
  ctx.font = '600 27px Arial, sans-serif';
  drawLines(wrapText(ctx, safe(data.fraudSummary) || palette.copy, 1320, 3), 125, resultY + 92, 27, 35, '#171923');

  if (hasFraudSection) {
    rounded(ctx, 95, fraudY, 1410, fraudHeight, 20, '#fff7f5', '#e7aaa3');
    drawLabel('Fraud / related evidence', 125, fraudY + 42, '#a83d35');
    const meta = [data.fraudSource ? `Source: ${safe(data.fraudSource)}` : '', data.fraudSeller ? `Seller: ${safe(data.fraudSeller)}` : '']
      .filter(Boolean).join(' · ');
    if (meta) {
      ctx.fillStyle = '#5e5251';
      ctx.font = '500 21px Arial, sans-serif';
      ctx.fillText(meta, 125, fraudY + 79);
    }
    if (loadedEvidence.length) {
      const gap = 14;
      const thumbWidth = (1320 - gap * 4) / 5;
      loadedEvidence.forEach((photo, index) => {
        const x = 125 + index * (thumbWidth + gap);
        const y = fraudY + 100;
        rounded(ctx, x, y, thumbWidth, 165, 12, '#ffffff', '#e7d5d2');
        drawContained(ctx, photo.image, x + 8, y + 8, thumbWidth - 16, 126);
        ctx.fillStyle = '#6b5a58';
        ctx.font = '700 14px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(safe(photo.label) || `EVIDENCE ${index + 1}`, x + thumbWidth / 2, y + 153);
      });
      if (data.fraudMoreCount && data.fraudMoreCount > 0) {
        ctx.fillStyle = '#8c3f38';
        ctx.font = '700 18px Arial, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(`+${data.fraudMoreCount} more in live record`, 1475, fraudY + fraudHeight - 18);
      }
    }
  }

  rounded(ctx, 95, footerY, 1410, 260, 20, '#f7f8fc', '#e0e2ea');
  let qrDrawn = false;
  try {
    const qrDataUrl = await QRCode.toDataURL(data.verificationUrl || GENERIC_VERIFY_URL, {
      width: 360,
      margin: 1,
      errorCorrectionLevel: 'M',
      color: { dark: '#0A0B16', light: '#FFFFFF' },
    });
    const qr = await loadImage(qrDataUrl);
    if (qr) {
      ctx.drawImage(qr, 1210, footerY + 24, 205, 205);
      qrDrawn = true;
    }
  } catch {
    // Integrity data remains printed when QR generation is unavailable.
  }
  drawLabel('Registered original', 125, footerY + 50);
  ctx.fillStyle = '#0a0b16';
  ctx.font = '800 33px Arial, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('Provider data + CheckCOA certificate-risk check', 125, footerY + 101);
  ctx.fillStyle = '#65697a';
  ctx.font = '400 22px Arial, sans-serif';
  const disclaimer = 'SHA-256 proves an exact match to the registered PNG bytes. A re-saved image or screenshot is a different file. Scan the QR to verify the original and current CheckCOA status.';
  drawLines(wrapText(ctx, disclaimer, qrDrawn ? 1000 : 1320, 3), 125, footerY + 150, 22, 30, '#65697a');
  ctx.fillStyle = '#74788a';
  ctx.font = '700 17px Arial, sans-serif';
  ctx.fillText(`CHECKCOA.COM · ORIGINAL ${safe(data.publicId || data.scanId).slice(0, 32)}`, 125, footerY + 226);

  if (data.publicId) {
    ctx.save();
    ctx.translate(WIDTH / 2, finalHeight / 2);
    ctx.rotate(-Math.PI / 7);
    ctx.globalAlpha = 0.045;
    ctx.fillStyle = '#293365';
    ctx.font = '800 36px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`REGISTERED ORIGINAL · ${data.publicId}`, 0, 0);
    ctx.restore();
  }
  if (addWatermark) {
    ctx.save();
    ctx.translate(WIDTH / 2, finalHeight / 2 + 90);
    ctx.rotate(-Math.PI / 7);
    ctx.globalAlpha = 0.12;
    ctx.fillStyle = '#70758c';
    ctx.font = '800 64px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('ACCOUNT COPY', 0, 0);
    ctx.restore();
  }

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('PNG export failed.')), 'image/png');
  });
}

export function downloadCertificate(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
